/**
 * Tree Utilities
 * Helper functions for working with hierarchical/tree data structures
 */

/**
 * Tree node with metadata for rendering
 */
export interface TreeNode<T> {
  /** Original data */
  data: T;

  /** Node level/depth (0-based, root = 0) */
  level: number;

  /** Whether this node is expanded */
  expanded: boolean;

  /** Whether this node has children */
  hasChildren: boolean;

  /** Parent node reference */
  parent?: TreeNode<T>;

  /** Children nodes */
  children?: TreeNode<T>[];

  /** Path from root (array of IDs or keys) */
  path: string[];

  /** Unique node ID */
  id: any;
}

/**
 * Tree configuration
 */
export interface TreeConfig<T> {
  /** Field containing children array */
  childrenField: keyof T;

  /** Field to use as unique ID */
  idField: keyof T;

  /** Initial expand depth (-1 = all, 0 = none, n = n levels) */
  expandDepth?: number;
}

/**
 * Flatten a tree structure into an array while maintaining hierarchy info
 * @param data - Array of root nodes
 * @param config - Tree configuration
 * @returns Flattened array of tree nodes with metadata
 */
export function flattenTree<T>(
  data: T[],
  config: TreeConfig<T>,
  parent: TreeNode<T> | null = null,
  level: number = 0
): TreeNode<T>[] {
  const result: TreeNode<T>[] = [];
  const expandDepth = config.expandDepth ?? 0;

  for (const item of data) {
    const children = (item[config.childrenField] as any) as T[] | undefined;
    const hasChildren = !!children && children.length > 0;
    const id = item[config.idField];

    // Build path
    const path = parent ? [...parent.path, String(id)] : [String(id)];

    // Create tree node
    const treeNode: TreeNode<T> = {
      data: item,
      level,
      expanded: expandDepth === -1 || level < expandDepth, // Auto-expand based on config
      hasChildren,
      parent: parent || undefined,
      children: undefined,
      path,
      id,
    };

    // Add to result
    result.push(treeNode);

    // Recursively flatten children if they exist
    if (hasChildren && children) {
      const childNodes = flattenTree(children, config, treeNode, level + 1);
      treeNode.children = childNodes;
      result.push(...childNodes);
    }
  }

  return result;
}

/**
 * Get visible nodes from flattened tree (only expanded nodes and their children)
 * @param flattenedTree - Flattened tree array
 * @returns Array of visible nodes
 */
export function getVisibleNodes<T>(flattenedTree: TreeNode<T>[]): TreeNode<T>[] {
  const visible: TreeNode<T>[] = [];

  for (const node of flattenedTree) {
    // Root level is always visible
    if (node.level === 0) {
      visible.push(node);
      continue;
    }

    // Check if all parents are expanded
    let current = node.parent;
    let allParentsExpanded = true;

    while (current) {
      if (!current.expanded) {
        allParentsExpanded = false;
        break;
      }
      current = current.parent;
    }

    if (allParentsExpanded) {
      visible.push(node);
    }
  }

  return visible;
}

/**
 * Toggle node expansion state
 * @param node - Node to toggle
 * @param flattenedTree - Full flattened tree
 * @returns Updated flattened tree
 */
export function toggleNodeExpansion<T>(
  node: TreeNode<T>,
  flattenedTree: TreeNode<T>[]
): TreeNode<T>[] {
  // Find the node in the tree and toggle it
  const nodeInTree = flattenedTree.find(n => n.id === node.id);
  if (nodeInTree) {
    nodeInTree.expanded = !nodeInTree.expanded;
  }
  return flattenedTree;
}

/**
 * Expand a node (and optionally all its children recursively)
 * @param node - Node to expand
 * @param flattenedTree - Full flattened tree
 * @param expandChildren - Also expand all descendants
 * @returns Updated flattened tree
 */
export function expandNode<T>(
  node: TreeNode<T>,
  flattenedTree: TreeNode<T>[],
  expandChildren: boolean = false
): TreeNode<T>[] {
  const nodeInTree = flattenedTree.find(n => n.id === node.id);
  if (nodeInTree) {
    nodeInTree.expanded = true;

    // Recursively expand all children
    if (expandChildren && nodeInTree.children) {
      for (const child of nodeInTree.children) {
        expandNode(child, flattenedTree, true);
      }
    }
  }
  return flattenedTree;
}

/**
 * Collapse a node (and optionally all its children recursively)
 * @param node - Node to collapse
 * @param flattenedTree - Full flattened tree
 * @param collapseChildren - Also collapse all descendants
 * @returns Updated flattened tree
 */
export function collapseNode<T>(
  node: TreeNode<T>,
  flattenedTree: TreeNode<T>[],
  collapseChildren: boolean = false
): TreeNode<T>[] {
  const nodeInTree = flattenedTree.find(n => n.id === node.id);
  if (nodeInTree) {
    nodeInTree.expanded = false;

    // Recursively collapse all children
    if (collapseChildren && nodeInTree.children) {
      for (const child of nodeInTree.children) {
        collapseNode(child, flattenedTree, true);
      }
    }
  }
  return flattenedTree;
}

/**
 * Expand all nodes in the tree
 * @param flattenedTree - Full flattened tree
 * @returns Updated flattened tree
 */
export function expandAll<T>(flattenedTree: TreeNode<T>[]): TreeNode<T>[] {
  for (const node of flattenedTree) {
    if (node.hasChildren) {
      node.expanded = true;
    }
  }
  return flattenedTree;
}

/**
 * Collapse all nodes in the tree
 * @param flattenedTree - Full flattened tree
 * @returns Updated flattened tree
 */
export function collapseAll<T>(flattenedTree: TreeNode<T>[]): TreeNode<T>[] {
  for (const node of flattenedTree) {
    if (node.hasChildren) {
      node.expanded = false;
    }
  }
  return flattenedTree;
}

/**
 * Expand path to a specific node (expand all parents)
 * @param node - Target node to reveal
 * @param flattenedTree - Full flattened tree
 * @returns Updated flattened tree
 */
export function expandToNode<T>(
  node: TreeNode<T>,
  flattenedTree: TreeNode<T>[]
): TreeNode<T>[] {
  let current = node.parent;

  while (current) {
    const nodeInTree = flattenedTree.find(n => n.id === current!.id);
    if (nodeInTree) {
      nodeInTree.expanded = true;
    }
    current = current.parent;
  }

  return flattenedTree;
}

/**
 * Get all descendant nodes of a given node
 * @param node - Parent node
 * @param flattenedTree - Full flattened tree
 * @returns Array of descendant nodes
 */
export function getDescendants<T>(
  node: TreeNode<T>,
  flattenedTree: TreeNode<T>[]
): TreeNode<T>[] {
  const descendants: TreeNode<T>[] = [];

  for (const treeNode of flattenedTree) {
    // Check if this node is a descendant
    if (treeNode.path.length > node.path.length) {
      // Check if node.path is a prefix of treeNode.path
      const isDescendant = node.path.every((segment, index) => treeNode.path[index] === segment);
      if (isDescendant) {
        descendants.push(treeNode);
      }
    }
  }

  return descendants;
}

/**
 * Get parent nodes (ancestors) of a given node
 * @param node - Child node
 * @param flattenedTree - Full flattened tree
 * @returns Array of ancestor nodes (from root to immediate parent)
 */
export function getAncestors<T>(
  node: TreeNode<T>,
  flattenedTree: TreeNode<T>[]
): TreeNode<T>[] {
  const ancestors: TreeNode<T>[] = [];
  let current = node.parent;

  while (current) {
    ancestors.unshift(current); // Add to beginning to maintain root-to-parent order
    current = current.parent;
  }

  return ancestors;
}

/**
 * Get max depth of the tree
 * @param flattenedTree - Full flattened tree
 * @returns Maximum depth/level
 */
export function getMaxDepth<T>(flattenedTree: TreeNode<T>[]): number {
  return flattenedTree.reduce((max, node) => Math.max(max, node.level), 0);
}

/**
 * Count total nodes (including hidden)
 * @param flattenedTree - Full flattened tree
 * @returns Total node count
 */
export function getTotalNodeCount<T>(flattenedTree: TreeNode<T>[]): number {
  return flattenedTree.length;
}

/**
 * Count visible nodes (only expanded)
 * @param flattenedTree - Full flattened tree
 * @returns Visible node count
 */
export function getVisibleNodeCount<T>(flattenedTree: TreeNode<T>[]): number {
  return getVisibleNodes(flattenedTree).length;
}

/**
 * Count expanded nodes
 * @param flattenedTree - Full flattened tree
 * @returns Expanded node count
 */
export function getExpandedNodeCount<T>(flattenedTree: TreeNode<T>[]): number {
  return flattenedTree.filter(node => node.expanded && node.hasChildren).length;
}

/**
 * Search tree nodes by predicate
 * @param flattenedTree - Full flattened tree
 * @param predicate - Search function
 * @returns Matching nodes
 */
export function searchTree<T>(
  flattenedTree: TreeNode<T>[],
  predicate: (node: TreeNode<T>) => boolean
): TreeNode<T>[] {
  return flattenedTree.filter(predicate);
}

/**
 * Rebuild tree from flattened data
 * Useful for converting back to hierarchical structure
 * @param flattenedTree - Flattened tree array
 * @param config - Tree configuration
 * @returns Root nodes with nested children
 */
export function rebuildTree<T>(
  flattenedTree: TreeNode<T>[],
  config: TreeConfig<T>
): T[] {
  const rootNodes = flattenedTree.filter(node => node.level === 0);

  const buildNode = (node: TreeNode<T>): T => {
    const data = { ...node.data };

    if (node.hasChildren && node.children) {
      const children = node.children
        .filter(child => child.level === node.level + 1 && child.parent?.id === node.id)
        .map(buildNode);

      (data as any)[config.childrenField] = children;
    }

    return data;
  };

  return rootNodes.map(buildNode);
}
